import {
  useContext,
  useCallback,
  useRef,
  useEffect,
  type ReactNode,
} from 'react';
import SnackbarContext from '@contexts/SnackbarContext';

const DEFAULT_FADE_OUT_MS = 5000;

interface ShowSnackbarOptions {
  text: string;
  position?: string;
  variant?: string;
  iconName?: string;
  /**
   * Optional React node rendered as the snackbar's leading icon. Takes
   * precedence over {@link iconName}, which only supports icons baked into
   * the SpriteSVG sprite. Use this when you want a one-off icon from
   * react-icons / a custom SVG without extending the sprite.
   */
  leadingIcon?: ReactNode;
  endingButtonTitle?: string;
  onEndingButtonClick?: () => void;
  duration?: number;
}

export default function useSnackbar() {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }

  const {
    snackbarData,
    snackbarId,
    snackbarIsOpen,
    snackbarPosition,
    setSnackbarData,
    setSnackbarId,
    setSnackbarIsOpen,
    setSnackbarPosition,
    resetSnackbar,
  } = context;

  const fadeOutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSnackbarFadeOutTimer = useCallback(() => {
    if (fadeOutTimerRef.current) {
      clearTimeout(fadeOutTimerRef.current);
      fadeOutTimerRef.current = null;
    }
  }, []);

  // Clear any pending timer on unmount to avoid stale callbacks
  useEffect(() => () => clearSnackbarFadeOutTimer(), [clearSnackbarFadeOutTimer]);

  const hideSnackbar = useCallback(() => {
    clearSnackbarFadeOutTimer();
    resetSnackbar();
  }, [clearSnackbarFadeOutTimer, resetSnackbar]);

  const startSnackbarFadeOutTimer = useCallback(
    (duration = DEFAULT_FADE_OUT_MS) => {
      clearSnackbarFadeOutTimer();
      fadeOutTimerRef.current = setTimeout(() => {
        hideSnackbar();
      }, duration);
    },
    [clearSnackbarFadeOutTimer, hideSnackbar],
  );

  const showSnackbar = useCallback(
    ({
      text,
      position = 'bottom left',
      variant,
      iconName,
      leadingIcon,
      endingButtonTitle,
      onEndingButtonClick,
      duration = DEFAULT_FADE_OUT_MS,
    }: ShowSnackbarOptions) => {
      clearSnackbarFadeOutTimer();

      setSnackbarData({
        text,
        variant,
        iconName,
        leadingIcon,
        endingButtonTitle,
        onEndingButtonClick,
        show: true,
      });
      setSnackbarPosition(position);
      setSnackbarId(`snackbar-${Date.now()}`);
      setSnackbarIsOpen(true);

      startSnackbarFadeOutTimer(duration);
    },
    [
      clearSnackbarFadeOutTimer,
      setSnackbarData,
      setSnackbarPosition,
      setSnackbarId,
      setSnackbarIsOpen,
      startSnackbarFadeOutTimer,
    ],
  );

  /** Show a success snackbar with a simple message string. */
  const showSuccess = useCallback(
    (message: string) => showSnackbar({ text: message }),
    [showSnackbar],
  );

  /** Show an error snackbar with a simple message string. */
  const showError = useCallback(
    (message: string) => showSnackbar({ text: message, variant: 'snackbarError' }),
    [showSnackbar],
  );

  return {
    snackbarData,
    snackbarId,
    snackbarIsOpen,
    snackbarPosition,
    showSnackbar,
    showSuccess,
    showError,
    hideSnackbar,
    startSnackbarFadeOutTimer,
    clearSnackbarFadeOutTimer,
  };
}
